import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { Navigation } from "swiper/modules";
import type { SwiperRef } from "swiper/react";

import { NEWSLETTER_BUNDLES } from "shared/data/newsletter";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import type { bundledNewsletterCategoryItemSlideshowNewsletterCategory$key } from "scmp-app/queries/__generated__/bundledNewsletterCategoryItemSlideshowNewsletterCategory.graphql";

import { BundledNewsletterItem } from "./bundled-newsletter-item";
import {
  ArrowContainer,
  ArrowNext,
  ArrowPrevious,
  Container,
  Header,
  List,
  StyledSwiper,
  StyledSwiperSlide,
} from "./styles";

export type Props = React.DetailsHTMLAttributes<HTMLDivElement> & {
  reference: bundledNewsletterCategoryItemSlideshowNewsletterCategory$key;
};

export const BundledNewsletterCategorySlideshow = forwardRef<HTMLDivElement, Props>(
  ({ reference, ...props }, forwardedReference) => {
    const { newsletters } = useFragment(
      graphql`
        fragment bundledNewsletterCategoryItemSlideshowNewsletterCategory on Query
        @argumentDefinitions(bundledNewslettersEntityIds: { type: "[String!]" }) {
          newsletters(first: 100, filter: { entityIds: $bundledNewslettersEntityIds }) {
            edges {
              node {
                entityId
                ...bundledNewsletterItemNewsletter
              }
            }
          }
        }
      `,
      reference,
    );

    const bundles = useMemo(() => {
      const newsletterMap = Object.fromEntries(
        newsletters.edges.map(({ node }) => [node.entityId, node]),
      );
      return Object.values(NEWSLETTER_BUNDLES).map(({ displayName, newsletters }) => ({
        displayName,
        newsletters: newsletters.map(id => newsletterMap[id]),
      }));
    }, [newsletters.edges]);

    const swiperReference = useRef<SwiperRef>(null);
    const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);
    const [isPreviousDisable, setIsPreviousDisable] = React.useState(true);
    const [isNextDisable, setIsNextDisable] = React.useState(false);

    const handleArrowClick = useCallback((direction: "next" | "prev") => {
      if (!swiperReference.current) return;

      switch (direction) {
        case "next":
          swiperReference.current.swiper.slideNext();
          break;
        case "prev":
          swiperReference.current.swiper.slidePrev();
          break;
      }
    }, []);

    useEffect(() => {
      if (!swiperReference.current) return;
      const instance = swiperReference.current.swiper;

      const updateButtonStates = () => {
        setIsPreviousDisable(instance.isBeginning);
        setIsNextDisable(instance.isEnd);
      };

      updateButtonStates();

      instance.on("slideChange", updateButtonStates);
      instance.on("reachBeginning", updateButtonStates);
      instance.on("reachEnd", updateButtonStates);
      instance.on("init", updateButtonStates);
      instance.on("resize", updateButtonStates);

      return () => {
        instance.off("slideChange", updateButtonStates);
        instance.off("reachBeginning", updateButtonStates);
        instance.off("reachEnd", updateButtonStates);
        instance.off("init", updateButtonStates);
        instance.off("resize", updateButtonStates);
      };
    }, []);

    return (
      <Container ref={forwardedReference} {...props}>
        <Header>
          Newsletter Bundles ({Object.keys(NEWSLETTER_BUNDLES).length})
          <ArrowContainer>
            <ArrowPrevious
              $isDisabled={isPreviousDisable || swiperReference.current?.swiper.isLocked}
              onClick={() => handleArrowClick("prev")}
            />
            <ArrowNext
              $isDisabled={isNextDisable || swiperReference.current?.swiper.isLocked}
              onClick={() => handleArrowClick("next")}
            />
          </ArrowContainer>
        </Header>

        <List>
          <StyledSwiper
            modules={[Navigation]}
            pagination={false}
            ref={swiperReference}
            slidesOffsetAfter={isTabletUp ? 0 : 20}
            slidesOffsetBefore={isTabletUp ? 0 : 20}
            slidesPerGroupAuto
            slidesPerView="auto"
            spaceBetween={20}
          >
            {bundles.map(({ displayName, newsletters }) => (
              <StyledSwiperSlide key={displayName}>
                <BundledNewsletterItem displayName={displayName} reference={newsletters} />
              </StyledSwiperSlide>
            ))}
          </StyledSwiper>
        </List>
      </Container>
    );
  },
);

BundledNewsletterCategorySlideshow.displayName = "BundledNewsletterCategorySlideshow";
